import React, { useState } from "react"
import { ADMIN_TYPE } from "../../models/user"
import { useForm } from "@mantine/form"
import { createUser } from "../api"
import { notifications } from "@mantine/notifications"
import { BaseModal, requiredMark, requireLabelStyles } from "./BaseModal"
import { Box, Button, Group, PasswordInput, ScrollArea, Select, Stack, TextInput } from "@mantine/core"
import { confirmDiscardChanges } from "../utils/confirm"

const EMAIL_REGEXP = /^[A-Za-z0-9]{1}[A-Za-z0-9_.+-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/

interface Props {
  opened: boolean
  onClose: () => void
  clientId: number
  onSaved?: () => void
}

interface FormValues {
  email: string
  password: string
  password_confirmation: string
  admin_type: string
}

const defaultValues: FormValues = {
  email: '',
  password: '',
  password_confirmation: '',
  admin_type: String(ADMIN_TYPE.GENERAL),
}

const ADMIN_TYPE_OPTIONS = [
  { value: String(ADMIN_TYPE.ADMIN), label: '管理者' },
  { value: String(ADMIN_TYPE.GENERAL), label: '一般' },
]

export const UserCreateModal: React.FC<Props> = ({ opened, onClose, clientId, onSaved }) => {
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<FormValues>({
    initialValues: defaultValues,
    validate: {
      email: (v) => {
        if (!v) return '必須です'
        if (!EMAIL_REGEXP.test(v)) return '正しいメールアドレスを入力してください'
        return null
      },
      password: (v) => {
        if (!v) return '必須です'
        if (v.length < 8 || v.length > 16) return '8～16文字で入力してください'
        return null
      },
      password_confirmation: (v, values) => {
        if (!v) return '必須です'
        if (v !== values.password) return 'パスワードが一致しません'
        return null
      },
      admin_type: (v) => (!v ? '必須です' : null),
    },
  })

  const handleClose = () => {
    if (form.isDirty() && !confirmDiscardChanges()) {
      return
    }
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)

    try {
      await createUser(
        clientId,
        values.email,
        Number(values.admin_type),
        values.password,
        values.password_confirmation,
      )
      notifications.show({ message: 'IDを新規作成しました', color: 'teal' })
      onSaved?.()
      onClose()
    } catch (errors: any) {
      const message = Array.isArray(errors) ? errors.join('\n') : 'IDの作成に失敗しました'
      notifications.show({ message, color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BaseModal
      opened={opened}
      onClose={handleClose}
      title="ID新規作成"
      onExited={() => {
        form.setValues(defaultValues)
        form.resetDirty()
      }}
    >
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <ScrollArea style={{ flex: 1, minHeight: 0 }} bg="#00000008" px={24}>
          <Stack gap="md" p="md">
            <TextInput
              label={<><span>メールアドレス</span>{requiredMark}</>}
              autoComplete="off"
              {...form.getInputProps('email')}
              styles={requireLabelStyles}
            />
            <PasswordInput
              label={<><span>パスワード</span>{requiredMark}</>}
              placeholder="8～16文字"
              autoComplete="new-password"
              {...form.getInputProps('password')}
              styles={requireLabelStyles}
            />
            <PasswordInput
              label={<><span>パスワード（確認）</span>{requiredMark}</>}
              placeholder="8～16文字"
              autoComplete="new-password"
              {...form.getInputProps('password_confirmation')}
              styles={requireLabelStyles}
            />
            <Select
              label={<><span>管理者権限</span>{requiredMark}</>}
              data={ADMIN_TYPE_OPTIONS}
              {...form.getInputProps('admin_type')}
              allowDeselect={false}
              styles={requireLabelStyles}
            />
          </Stack>
        </ScrollArea>

        <Box
          p="md"
          style={{
            borderTop: '1px solid #0000001A',
            backgroundColor: '#fff',
            flexShrink: 0,
          }}
        >
          <Group justify="flex-end" gap={8}>
            <Button
              variant="outline"
              radius="xl"
              color="gray"
              onClick={handleClose}
              disabled={submitting}
            >
              キャンセル
            </Button>
            <Button
              variant="filled"
              radius="xl"
              type="submit"
              loading={submitting}
            >
              新規作成
            </Button>
          </Group>
        </Box>
      </form>
    </BaseModal>
  )
}
